import { Vue, Component } from "vue-property-decorator";
import { Action, namespace } from "vuex-class";
import { EXTERNAL_APP_URI, EMAIL_EDITOR_ORIGIN } from "../../common/uri/property-uri.constants";
import { LanguageHelper } from "../../utils/language-helper";
import { storeModules } from "../../store";
import { TAB_VIEW_ACTIONS, SetUnsavedDataParamModel } from "../../store/tab-view/tab-view-types.store";
import { TabViewModel } from "../../models/common/tab-view.model";
import { ROUTER } from "../../common/constants";
import EventBus from "../../utils/event-bus";
import { embeddedAppTokenBroker } from "../../utils/embedded-app-token";
import DataChangeConfirmModal from "../controls/data-change-confirmation";
import {
	EMAIL_EDITOR_EVENTS,
	WEBAPP_MESSAGE_SOURCE,
	EMAIL_EDITOR_LAST_PATH_KEY,
	EMAIL_TAB_IDENTIFY_DATA
} from "../../common/email-editor.constant";

const TabViewAction = namespace(storeModules.TabView, Action);

@Component({
	// Must match CACHE_COMPONENT_NAME so keep-alive preserves this instance across tab switches.
	name: ROUTER.EMAIL_WEBSITE,
	template: require("./template.html"),
	components: {
		"data-change-confirm-modal": DataChangeConfirmModal
	}
})
export default class EmailWebsite extends Vue {
	@TabViewAction(TAB_VIEW_ACTIONS.SET_UNSAVED_DATA_STATE) private setUnsavedData: (payload: SetUnsavedDataParamModel) => void;
	@TabViewAction(TAB_VIEW_ACTIONS.SET_CALLBACK_CLOSE_TAB_VIEW) private setCallbackCloseTab: (payload: { tab: TabViewModel, callback: any }) => void;

	emailAppUrl = "";
	isLoading = true;
	hasUnsavedChanges = false;
	unsavedWarningVisible = false;
	private wasDeactivated = false;
	private allowLeave = false;
	private pendingRoutePath: string = null;
	private unregisterAccessToken: () => void = null;

	mounted() {
		this.isLoading = true;
		this.loadEmailApp();
		window.addEventListener("message", this.handleEditorMessage);
		// Under keep-alive this registration lives until beforeDestroy; a detached iframe has no
		// contentWindow, so nothing is posted to it.
		this.unregisterAccessToken = embeddedAppTokenBroker.register({
			origin: EMAIL_EDITOR_ORIGIN,
			getContentWindow: () => (this.$refs.emailIframe as HTMLIFrameElement)?.contentWindow || null,
			getLanguageId: () => LanguageHelper.convertLanguageInt(this.$i18n.locale)
		});
	}

	activated() {
		// keep-alive detaches the iframe on tab switch and a detached iframe reloads when re-inserted,
		// so rebuild the URL from the last reported editor path; the reloaded editor handshakes again.
		if (this.wasDeactivated) {
			this.wasDeactivated = false;
			this.isLoading = true;
			this.loadEmailApp();
		}
		// The tab entry may have just been (re)created by the tab-view route watcher, so wait a tick.
		this.$nextTick(() => {
			this.updateUnsavedDataState(this.hasUnsavedChanges);
			this.registerCloseGuard();
			this.verifyCloseableRequest();
		});
	}

	deactivated() {
		this.wasDeactivated = true;
	}

	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges || this.allowLeave) {
			this.allowLeave = false;
			next();
			return;
		}
		this.pendingRoutePath = to.fullPath;
		this.unsavedWarningVisible = true;
		next(false);
	}

	beforeDestroy() {
		window.removeEventListener("message", this.handleEditorMessage);
		this.unregisterAccessToken?.();
		// The iframe (and any draft) dies with this instance; don't leave a stale warning on the tab.
		// hasUnsavedChanges must reset too so a leftover close callback falls through to a plain close.
		this.hasUnsavedChanges = false;
		this.updateUnsavedDataState(false);
	}

	loadEmailApp() {
		this.emailAppUrl = `${EXTERNAL_APP_URI.EMAIL_APP}${sessionStorage.getItem(EMAIL_EDITOR_LAST_PATH_KEY) || ""}`;
	}

	// Event handler when the iframe finishes loading
	onIframeLoad() {
		this.isLoading = false;
	}

	private handleEditorMessage(event: MessageEvent) {
		if (event.origin !== EMAIL_EDITOR_ORIGIN) return;
		const iframe = this.$refs.emailIframe as HTMLIFrameElement;
		if (!iframe?.contentWindow || event.source !== iframe.contentWindow) return;

		const data = event.data;
		switch (data?.event) {
			case EMAIL_EDITOR_EVENTS.DIRTY_STATE:
				this.hasUnsavedChanges = !!data.payload?.dirty;
				this.updateUnsavedDataState(this.hasUnsavedChanges);
				this.registerCloseGuard();
				break;
			case EMAIL_EDITOR_EVENTS.LOCATION_CHANGED:
				if (typeof data.payload?.path === "string") {
					sessionStorage.setItem(EMAIL_EDITOR_LAST_PATH_KEY, data.payload.path);
				}
				break;
		}
	}

	private updateUnsavedDataState(value: boolean) {
		this.setUnsavedData({
			identifyData: EMAIL_TAB_IDENTIFY_DATA,
			value
		});
	}

	// The tab is restored from localforage asynchronously on app load, so activated() can run before the
	// tab exists and the registration no-op; re-registering on every dirty-state message makes it reliable.
	private registerCloseGuard() {
		this.setCallbackCloseTab({
			tab: { identifyData: EMAIL_TAB_IDENTIFY_DATA } as TabViewModel,
			callback: () => this.handleBeforeClose()
		});
	}

	// Closing a background tab with unsaved data routes here with closeable=1 (see tab-views removeTab).
	private verifyCloseableRequest() {
		if (!this.$route.query.closeable) return;
		this.handleBeforeClose();
	}

	private handleBeforeClose() {
		if (this.hasUnsavedChanges) {
			this.unsavedWarningVisible = true;
		} else {
			this.closeEmailTab();
		}
	}

	onLeaveWithoutSaving() {
		this.unsavedWarningVisible = false;
		// The user explicitly discarded; if the editor restores the draft after the
		// iframe reload it re-sends dirty-state=true and the flags come back.
		this.hasUnsavedChanges = false;
		this.updateUnsavedDataState(false);
		if (this.pendingRoutePath) {
			// Tab-switch context: continue the navigation the route guard blocked.
			const path = this.pendingRoutePath;
			this.pendingRoutePath = null;
			this.allowLeave = true;
			this.$router.push(path);
			return;
		}
		this.allowLeave = true;
		this.closeEmailTab();
	}

	onSaveInEditor() {
		this.unsavedWarningVisible = false;
		this.pendingRoutePath = null;
		this.postSaveRequest();
		// Stay on the tab; the editor confirms the save with a dirty-state=false message.
		this.clearCloseableQuery();
		EventBus.$emit("cancel-close-tab");
	}

	onStayInEditor() {
		this.unsavedWarningVisible = false;
		this.pendingRoutePath = null;
		this.clearCloseableQuery();
		EventBus.$emit("cancel-close-tab");
	}

	private postSaveRequest() {
		const iframe = this.$refs.emailIframe as HTMLIFrameElement;
		if (iframe?.contentWindow) {
			iframe.contentWindow.postMessage(
				{ source: WEBAPP_MESSAGE_SOURCE, event: EMAIL_EDITOR_EVENTS.SAVE_REQUEST },
				EMAIL_EDITOR_ORIGIN
			);
		}
	}

	private closeEmailTab() {
		EventBus.$emit("close-tab-view", { identifyData: EMAIL_TAB_IDENTIFY_DATA });
	}

	private clearCloseableQuery() {
		if (!this.$route.query.closeable) return;
		const query = { ...this.$route.query };
		delete query.closeable;
		this.$router.replace({ query });
	}
}
